import { useState, useEffect, useRef, useCallback } from 'react';
import styles from './Finder.module.css';
import { useWindowManager, WindowId } from '../Window/WindowManager';
import { useWindowResize } from '../Window/useWindowResize';
import { ResizeHandles } from '../Window/ResizeHandles';
import { WindowControls } from '../Window/WindowControls';
import { useFileSystem, HOME_DIR, type FileNode } from '../Window/FileSystem';
import { FolderIcon, FileIcon } from '../icons';
import type { ReactNode } from 'react';

interface FinderProps {
  onOpenFile?: (path: string, content: string) => void;
}

export function Finder({ onOpenFile }: FinderProps) {
  const { listDirectory, getNode, resolvePath, getParentPath } = useFileSystem();
  const [currentPath, setCurrentPath] = useState(HOME_DIR);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [pathHistory, setPathHistory] = useState<string[]>([HOME_DIR]);
  const [historyIndex, setHistoryIndex] = useState(0);

  // Window manager - start closed
  const {
    zIndex,
    focus,
    close,
    minimize,
    maximize,
    isMinimized,
    isClosed,
    isMaximized,
  } = useWindowManager(WindowId.Finder, 'Finder', <FolderIcon />, 'closed');

  // Window resize
  const { size, position, setPosition, isResizing, handleResizeStart } = useWindowResize(
    { width: 600, height: 400 },
    { x: 150, y: 80 },
    { minWidth: 400, minHeight: 250 }
  );

  // Dragging
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const finderRef = useRef<HTMLDivElement>(null);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (!finderRef.current) return;
    if (isMaximized) return;
    focus();
    const rect = finderRef.current.getBoundingClientRect();
    dragOffset.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
    setIsDragging(true);
  }, [focus, isMaximized]);

  useEffect(() => {
    if (!isDragging) return;

    const handlePointerMove = (e: PointerEvent) => {
      const desktopEl = document.querySelector('[data-desktop]') as HTMLElement;
      if (!desktopEl) return;

      const desktopRect = desktopEl.getBoundingClientRect();
      const finderEl = finderRef.current;
      if (!finderEl) return;

      const finderWidth = finderEl.offsetWidth;
      const finderHeight = finderEl.offsetHeight;

      let newX = e.clientX - desktopRect.left - dragOffset.current.x;
      let newY = e.clientY - desktopRect.top - dragOffset.current.y;

      const dockHeight = 56;
      newX = Math.max(0, Math.min(newX, desktopRect.width - finderWidth));
      newY = Math.max(0, Math.min(newY, desktopRect.height - finderHeight - dockHeight));

      setPosition({ x: newX, y: newY });
    };

    const handlePointerUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [isDragging, setPosition]);

  // Navigation functions
  const navigateTo = useCallback((path: string) => {
    const newPath = resolvePath(path, currentPath);
    const node = getNode(newPath);

    if (node && node.type === 'directory') {
      setCurrentPath(newPath);
      setSelectedItem(null);

      // Update history
      const newHistory = pathHistory.slice(0, historyIndex + 1);
      newHistory.push(newPath);
      setPathHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    }
  }, [currentPath, resolvePath, getNode, pathHistory, historyIndex]);

  const goBack = useCallback(() => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setCurrentPath(pathHistory[historyIndex - 1]);
      setSelectedItem(null);
    }
  }, [historyIndex, pathHistory]);

  const goForward = useCallback(() => {
    if (historyIndex < pathHistory.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setCurrentPath(pathHistory[historyIndex + 1]);
      setSelectedItem(null);
    }
  }, [historyIndex, pathHistory]);

  const goUp = useCallback(() => {
    const parentPath = getParentPath(currentPath);
    if (parentPath !== currentPath) {
      navigateTo(parentPath);
    }
  }, [currentPath, getParentPath, navigateTo]);

  // Handle item interactions
  const handleItemClick = useCallback((name: string) => {
    setSelectedItem(name);
  }, []);

  const handleItemDoubleClick = useCallback((name: string) => {
    const itemPath = `${currentPath}/${name}`;
    const node = getNode(itemPath);

    if (node) {
      if (node.type === 'directory') {
        navigateTo(name);
      } else if (node.type === 'file' && onOpenFile && node.content !== undefined) {
        onOpenFile(itemPath, node.content);
      }
    }
  }, [currentPath, getNode, navigateTo, onOpenFile]);

  // Get current directory contents
  const items = listDirectory(currentPath) || [];

  // Get icon for file type
  const getFileIcon = (node: FileNode): ReactNode => {
    if (node.type === 'directory') return <FolderIcon />;
    return <FileIcon />;
  };

  // Get display name for path
  const getDisplayPath = (path: string): string => {
    return path.replace('/home/visitor', '~');
  };

  // Close animation
  const [isClosing, setIsClosing] = useState(false);

  // Button handlers
  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsClosing(true);
  };

  const handleAnimationEnd = useCallback((e: React.AnimationEvent) => {
    if (e.animationName === styles['windowClose']) {
      setIsClosing(false);
      close();
    }
  }, [close]);

  const handleMinimize = (e: React.MouseEvent) => {
    e.stopPropagation();
    minimize();
  };

  const handleMaximize = (e: React.MouseEvent) => {
    e.stopPropagation();
    maximize();
  };

  if (isClosed || isMinimized) {
    return null;
  }

  const windowStyle = isMaximized
    ? {
        left: 0,
        top: 0,
        width: '100%',
        height: 'calc(100% - 56px)',
        zIndex,
      }
    : {
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
        zIndex,
      };

  return (
    <div
      ref={finderRef}
      className={`${styles.finder} ${isDragging || isResizing ? styles.dragging : ''} ${isMaximized ? styles.maximized : ''} ${isClosing ? styles.closing : ''}`}
      style={windowStyle}
      onPointerDown={focus}
      onAnimationEnd={handleAnimationEnd}
    >
      {!isMaximized && <ResizeHandles onResizeStart={handleResizeStart} />}

      {/* Title bar */}
      <div className={styles.finderHeader} onPointerDown={handlePointerDown}>
        <WindowControls
          onClose={handleClose}
          onMinimize={handleMinimize}
          onMaximize={handleMaximize}
        />
        <span className={styles.finderTitle}>{getDisplayPath(currentPath)}</span>
      </div>

      {/* Toolbar */}
      <div className={styles.finderToolbar}>
        <button
          className={styles.finderNavButton}
          onClick={goBack}
          disabled={historyIndex <= 0}
          aria-label="Go back"
        >
          ◀
        </button>
        <button
          className={styles.finderNavButton}
          onClick={goForward}
          disabled={historyIndex >= pathHistory.length - 1}
          aria-label="Go forward"
        >
          ▶
        </button>
        <button
          className={styles.finderNavButton}
          onClick={goUp}
          aria-label="Go up"
        >
          ▲
        </button>
        <div className={styles.finderPathBar}>
          {getDisplayPath(currentPath)}
        </div>
      </div>

      {/* File listing */}
      <div className={styles.finderBody}>
        {items.length === 0 ? (
          <div className={styles.finderEmpty}>This folder is empty</div>
        ) : (
          <div className={styles.finderGrid}>
            {items.map((item) => (
              <div
                key={item.name}
                className={`${styles.finderItem} ${selectedItem === item.name ? styles.selected : ''}`}
                onClick={() => handleItemClick(item.name)}
                onDoubleClick={() => handleItemDoubleClick(item.name)}
              >
                <span className={styles.finderItemIcon}>{getFileIcon(item)}</span>
                <span className={styles.finderItemName}>{item.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Status bar */}
      <div className={styles.finderStatusBar}>
        {items.length} item{items.length !== 1 ? 's' : ''}
        {selectedItem && ` • "${selectedItem}" selected`}
      </div>
    </div>
  );
}
